import { CellValue } from './types';
import { RunFile, RunReading, RunSnapshot, RunStep, firstValue, sameIdentifier, toNumber } from './runs';
import { RoleKind, roleLabel, roleSpec } from './roles';
import { v } from './schema';

/**
 * What a report reads from a procedure, declared rather than buried in string
 * lookups, so it can be checked against the procedure before any run exists.
 *
 * A definition lists its requirements (`requires`). Each is matched to the
 * procedure's parameters in this order:
 *
 *   1. the definition's own binding table (`settings.bindings`), where the
 *      report's author named a parameter for a procedure that needs it
 *   2. an identifier: the requirement's own, then the role's well-known ones
 *      (roles.ts)
 *
 * Only the report's author binds, in code. The template page shows the result
 * against the procedure template, so a miss is a red row there rather than an
 * empty section in a published record. The same resolution runs over each run
 * the report is written on.
 */

export interface Requirement {
  label: string;
  /** the role that plays it (roles.ts); its well-known identifiers bind it */
  role?: string;
  /** identifiers to try, besides the role's well-known ones */
  identifiers?: string[];
  kind?: RoleKind;
  /** the report works without it; a miss is not flagged */
  optional?: boolean;
  /** every reading that plays it, in step order, rather than the first */
  many?: boolean;
  help?: string;
}

type NeedOptions = Omit<Requirement, 'label' | 'kind'> & { label?: string };

function requirement(kind: RoleKind, label: string, options: Omit<Requirement, 'label' | 'kind'> = {}): Requirement {
  return { label, kind, ...options };
}

export const need = {
  /** Whatever parameter plays a role, labelled as the role is unless said. */
  role(role: string, options: NeedOptions = {}): Requirement {
    const { label, ...rest } = options;
    return { label: label ?? roleLabel(role), role, kind: roleSpec(role)?.kind, ...rest };
  },
  text: (label: string, options?: Omit<Requirement, 'label' | 'kind'>) => requirement('text', label, options),
  number: (label: string, options?: Omit<Requirement, 'label' | 'kind'>) => requirement('number', label, options),
  flag: (label: string, options?: Omit<Requirement, 'label' | 'kind'>) => requirement('flag', label, options),
  file: (label: string, options?: Omit<Requirement, 'label' | 'kind'>) => requirement('file', label, options),
};

/** The report's own answer for one requirement, in its binding table: this parameter, or "not in this procedure". */
export interface ExplicitBinding {
  identifier?: string;
  /** the step's title, where the identifier repeats across steps; empty for any step */
  step?: string;
  /** the procedure has no such parameter: stop looking, and don't flag it */
  none?: boolean;
}

export type Bindings = Record<string, ExplicitBinding>;

export type BindingSource = 'template' | 'identifier' | 'none';

export interface BindingMatch {
  step: RunStep;
  reading: RunReading;
}

export interface ResolvedBinding {
  key: string;
  requirement: Requirement;
  source: BindingSource;
  matches: BindingMatch[];
}

const isReference = (r: RunReading) => r.operationType === 'File';

function allMatches(run: RunSnapshot, test: (reading: RunReading, step: RunStep) => boolean): BindingMatch[] {
  return run.steps.flatMap((step) => step.readings.filter((reading) => test(reading, step)).map((reading) => ({ step, reading })));
}

/** One requirement against one run (or the procedure template, read as a run with no values). */
export function resolveBinding(key: string, requirement: Requirement, run: RunSnapshot, explicit?: ExplicitBinding | null): ResolvedBinding {
  const done = (source: BindingSource, matches: BindingMatch[]): ResolvedBinding => ({ key, requirement, source: matches.length || source === 'template' ? source : 'none', matches: requirement.many ? matches : matches.slice(0, 1) });
  const recorded = (r: RunReading) => requirement.role === 'reference' || !isReference(r);

  if (explicit?.none) return { key, requirement, source: 'template', matches: [] };
  if (explicit?.identifier) {
    const wanted = explicit.identifier;
    const step = explicit.step?.trim().toLowerCase();
    return done('template', allMatches(run, (r, s) => sameIdentifier(r.identifier, wanted) && (!step || s.title.trim().toLowerCase() === step)));
  }
  const identifiers = [...(requirement.identifiers ?? []), ...(roleSpec(requirement.role)?.identifiers ?? [])];
  if (identifiers.length) {
    // the first identifier that matches anything wins, so a preferred spelling beats a fallback
    for (const id of identifiers) {
      const found = allMatches(run, (r) => recorded(r) && sameIdentifier(r.identifier, id));
      if (found.length) return done('identifier', found);
    }
  }
  return { key, requirement, source: 'none', matches: [] };
}

export function resolveBindings(requires: Record<string, Requirement> | undefined, run: RunSnapshot, bindings: Bindings = {}): Record<string, ResolvedBinding> {
  return Object.fromEntries(Object.entries(requires ?? {}).map(([key, req]) => [key, resolveBinding(key, req, run, bindings[key])]));
}

/** Requirements the report needs and the procedure doesn't hold, for the template page's check and a report's own finding. */
export function unboundRequirements(resolved: Record<string, ResolvedBinding>): ResolvedBinding[] {
  return Object.values(resolved).filter((b) => b.source === 'none' && !b.requirement.optional);
}

function normalizeExplicit(raw: unknown): ExplicitBinding | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.none === true) return { none: true };
  if (typeof r.identifier !== 'string' || r.identifier.trim() === '') return null;
  return typeof r.step === 'string' && r.step.trim() ? { identifier: r.identifier.trim(), step: r.step.trim() } : { identifier: r.identifier.trim() };
}

export function normalizeBindings(raw: unknown): Bindings {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Bindings = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const b = normalizeExplicit(value);
    if (b) out[key] = b;
  }
  return out;
}

/**
 * The binding table, as a settings field: written by the report's author
 * against the procedures it is for, as the field's default.
 *
 *   bindings: bindingsField({ default: { current: { identifier: 'measured_current_a' } } })
 */
export const bindingsField = (options: { label?: string; help?: string; default?: Bindings } = {}) =>
  v.custom<Bindings>('bindings', () => normalizeBindings(options.default ?? {}), normalizeBindings, { label: options.label ?? 'Bindings', help: options.help });

// ── reading what a binding found ────────────────────────────────────────────

export function boundValue(binding: ResolvedBinding | undefined): CellValue {
  for (const m of binding?.matches ?? []) {
    const value = firstValue(m.reading);
    if (value !== null) return value;
  }
  return null;
}

export function boundNumber(binding: ResolvedBinding | undefined): number | null {
  return toNumber(boundValue(binding));
}

export function boundText(binding: ResolvedBinding | undefined): string | null {
  const value = boundValue(binding);
  return value === null ? null : String(value);
}

export function boundFiles(binding: ResolvedBinding | undefined): Array<{ file: RunFile; step: RunStep; reading: RunReading }> {
  return (binding?.matches ?? []).flatMap((m) => (m.reading.attachments ?? []).map((file) => ({ file, step: m.step, reading: m.reading })));
}

// ── picking a reading inside a step, for a block's properties ───────────────

/** How a block's property names a reading: by role, or by identifier. */
export interface ReadingSelector {
  role?: string;
  identifier?: string;
}

export function normalizeSelector(raw: unknown): ReadingSelector {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  const out: ReadingSelector = {};
  if (typeof r.role === 'string' && r.role) out.role = r.role;
  if (typeof r.identifier === 'string' && r.identifier.trim()) out.identifier = r.identifier.trim();
  return out;
}

export const readingField = (options: { label?: string; help?: string; optional?: boolean } = {}) => v.custom<ReadingSelector>('reading', () => ({}), normalizeSelector, options);

export function isEmptySelector(selector: ReadingSelector): boolean {
  return !selector.role && !selector.identifier;
}

/** The readings in one step a selector names: by identifier if it gives one, else by the role's identifiers. */
export function selectReadings(step: RunStep, selector: ReadingSelector): RunReading[] {
  if (selector.identifier) return step.readings.filter((r) => sameIdentifier(r.identifier, selector.identifier as string));
  if (!selector.role) return [];
  const ids = roleSpec(selector.role)?.identifiers ?? [];
  return step.readings.filter((r) => !isReference(r) && ids.some((id) => sameIdentifier(r.identifier, id)));
}

export function describeSelector(selector: ReadingSelector): string {
  if (selector.identifier) return selector.identifier;
  if (selector.role) return roleLabel(selector.role);
  return '';
}
